import { FC, useCallback } from 'react';
import { useHistory } from 'react-router';
import { useMutation } from '@apollo/client';

import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import Column from 'components/Column';
import useToast from 'components/ToastProvider/useToast';

import NavigationHeader from '../../../../../components/NavigationHeader';
import { CreditCard } from '../../../../../hooks/useCreditCards';
import locales from './locales';
import {
  Body,
  CancelLink,
  Footer,
  TextContainer,
  TitleContainer,
} from './styles';
import { UnblockCreditCardPinDocument } from './unblockPin.gql';

interface UnblockPinProps {
  creditCard: CreditCard;
}

const UnblockPin: FC<UnblockPinProps> = ({ creditCard }) => {
  const { showToast } = useToast();
  const history = useHistory();

  const { creditCardId } = creditCard;

  const [unblockCreditCardPin, { loading }] = useMutation(
    UnblockCreditCardPinDocument,
  );

  const onConfirm = useCallback(async () => {
    try {
      await unblockCreditCardPin({
        variables: {
          input: { creditCardId },
        },
      });
      showToast({ message: locales.successMessage, type: 'success' });
      history.push(`/cards/${creditCardId}`);
    } catch (err) {
      showToast({ message: locales.errorMessage, type: 'error' });
    }
  }, [creditCardId, history, showToast, unblockCreditCardPin]);

  return (
    <Column>
      <Body>
        <NavigationHeader backTo={`/cards/${creditCardId}`} />
        <TextContainer>
          <TitleContainer>
            <Heading>{locales.title}</Heading>
          </TitleContainer>
          <Text>{locales.subtitle}</Text>
        </TextContainer>
      </Body>
      <Footer>
        <Button
          isLoading={loading}
          marginBottom="space-8"
          onClick={onConfirm}
          width="100%"
        >
          {locales.cta}
        </Button>
        <CancelLink disabled={loading} to={`/cards/${creditCardId}`}>
          {locales.cancel}
        </CancelLink>
      </Footer>
    </Column>
  );
};

export default UnblockPin;
